/**
 * Frontend client for the leaderboard API. Vanilla fetch + AbortController.
 *
 * Build-time kill switch: VITE_LEADERBOARD_ENABLED. When false, no requests
 * are fired and isLeaderboardEnabled() returns false.
 *
 * Runtime kill switch: if /api/score-start returns 503 with `{disabled:true}`,
 * the strip is hidden for that session — but the user's puzzle play is
 * unaffected. The frontend is fully graceful: any error path == strip hidden.
 */

import type {
  ScoreSubmitRequest,
  ScoreSubmitResponse,
  StartTokenResponse,
} from '@/types/leaderboard';

const API_BASE = '/api';
const SUBMIT_TIMEOUT_MS = 4000;
const START_TIMEOUT_MS = 3000;

interface CachedToken {
  token: string;
  expires_at_ms: number;
  puzzle_id: string;
}

let cachedToken: CachedToken | null = null;
let serverDisabled = false;

export function isLeaderboardEnabled(): boolean {
  if (serverDisabled) return false;
  // import.meta.env.VITE_LEADERBOARD_ENABLED is inlined by Vite at build time.
  return import.meta.env.VITE_LEADERBOARD_ENABLED === 'true';
}

/**
 * TEST-ONLY. Resets module-scope state so e2e tests don't leak between cases.
 */
export function __resetLeaderboardState(): void {
  cachedToken = null;
  serverDisabled = false;
}

/**
 * Fetch a start token. Returns null if leaderboard disabled, request fails,
 * timeout exceeded, or server returns 503 (server-side kill).
 */
export async function fetchStartToken(puzzleId: string): Promise<string | null> {
  if (!isLeaderboardEnabled()) return null;
  if (cachedToken && cachedToken.puzzle_id === puzzleId && cachedToken.expires_at_ms > Date.now() + 30_000) {
    return cachedToken.token;
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), START_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/score-start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ puzzle_id: puzzleId }),
      signal: ctrl.signal,
      credentials: 'omit',
    });
    clearTimeout(timer);
    if (res.status === 503) {
      // Server runtime kill switch.
      try {
        const body = (await res.json()) as { disabled?: boolean };
        if (body?.disabled) serverDisabled = true;
      } catch {
        // Treat any 503 as disabled.
        serverDisabled = true;
      }
      return null;
    }
    if (!res.ok) return null;
    const data = (await res.json()) as StartTokenResponse;
    cachedToken = {
      token: data.token,
      expires_at_ms: Date.parse(data.expires_at),
      puzzle_id: puzzleId,
    };
    return data.token;
  } catch {
    clearTimeout(timer);
    return null;
  }
}

export async function submitScore(req: ScoreSubmitRequest): Promise<ScoreSubmitResponse | null> {
  if (!isLeaderboardEnabled()) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), SUBMIT_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/score-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
      signal: ctrl.signal,
      credentials: 'omit',
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    const data = (await res.json()) as ScoreSubmitResponse;
    if (!data.accepted && data.reason === 'disabled') {
      serverDisabled = true;
    }
    return data;
  } catch {
    clearTimeout(timer);
    return null;
  } finally {
    // Token has been consumed by the server (or assumed consumed on network error).
    // Invalidate cache so a subsequent submit (e.g. anon then named claim) fetches a fresh token.
    cachedToken = null;
  }
}

/**
 * Compute completion_hash via Web Crypto. Matches the server-side check:
 *   sha256(`${puzzle_id}:${seconds}:${first_try_count}:${token}`)
 *
 * Returns 64-char lowercase hex.
 */
export async function computeCompletionHash(
  puzzleId: string,
  seconds: number,
  firstTryCount: number,
  token: string,
): Promise<string> {
  const data = new TextEncoder().encode(`${puzzleId}:${seconds}:${firstTryCount}:${token}`);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
